import { useState, useRef, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { message } from "antd";
import { CHAT_V2_API } from "./constants";
import { mapChatItem, fetchChatFilters, defaultChatFilterIndex, chatFilterSubConditions, fetchChatFilterSettings, isAssignedToMeFilterTab } from "./helpers";

const LARGE_LIST_THRESHOLD = 300;
// Date-range scan governance (see runDateRangeScan below): WATI's chat list
// has no server-side "last message time between X and Y" filter, so an
// arbitrary from/to range is serviced by paging the normal list (newest
// first) until either the oldest loaded chat falls before `from` (the
// window is fully captured) or these limits kick in. WARN pauses and asks
// before continuing; CAP is a hard stop that can't be overridden.
const DATE_SCAN_WARN_THRESHOLD = LARGE_LIST_THRESHOLD;
const DATE_SCAN_HARD_CAP = 1500;

// Reusable fetch/paginate/filter logic for a WATI chat list, scoped to a
// specific channel (or WATI's default channel when channelIds is omitted).
// Used both by WatiChatContext (customer channel, shared across QuerysetV4/
// Operations) and standalone by WhatsappMessages (supplier channel) —
// same logic, deliberately separate state/data per channel, since the two
// channels serve genuinely different volumes and use cases.
//
// `applyChatSettings` (default true) gates whether the admin's Chat
// Settings (allowed filters / default filter / admin exemption) apply to
// this instance at all. Explicit and caller-controlled rather than
// inferred from whether channelIds is present — the supplier channel's own
// channelIds resolves asynchronously (starts undefined on first render,
// same shape as the customer channel omitting it entirely), so presence
// alone can't reliably tell the two apart on that first render. When
// false, this instance skips the wait entirely (see the resolution effect
// below) — no admin restrictions, no delay, always plain "All Chats".
//
// `seed` (optional) lets a caller with applyChatSettings: false show an
// already-loaded background preload instantly instead of waiting on its
// own fetch — see WatiChatContext's supplier-channel preload. Only ever
// consumed when applyChatSettings is false; the customer channel always
// does its own explicit, settings-aware fetch regardless of any seed.
//
// `requireChannelIds` (default false) blocks the resolution effect below
// from running at all until channelIds is a non-empty array. The supplier
// channel's channelIds resolves asynchronously (starts undefined on the
// very first render, before WatiChatContext's fetchChannelAssignments call
// returns) — without this guard, that first render's effect fired a real
// fetch/seed-check with channelIds still undefined, which silently omitted
// the channel filter and pulled back the *other* channel's (customer's)
// chats. That wrong request and the follow-up correct one (once channelIds
// actually resolved) then raced over the network, and whichever landed
// last won — sometimes leaving the wrong channel's chats stuck on screen
// until the component remounted. The customer channel intentionally never
// sets this (it always omits channelIds by design, not "not yet resolved"),
// so it isn't affected.
// `persistKey` (optional) — when set, the sidebar's own filter picks (named
// tab, agent multi-select, status multi-select) survive a browser relaunch
// via localStorage, namespaced per instance so the customer and supplier
// channels don't clobber each other's saved picks. Deliberately separate
// from resetToDefaultFilter below (the reconnect flow's "back to default"
// action) — reconnecting after a brief outage shouldn't silently wipe out
// a saved preference the same way an explicit "reset filters" click should.
export function useWatiChatList({ authState, channelIds, applyChatSettings = true, seed, requireChannelIds = false, persistKey } = {}) {
  const { list: seedList = [], token: seedToken = null, count: seedCount = null } = seed || {};
  const LS_PREFIX = persistKey ? `qv4_chatfilters_${persistKey}_` : null;

  const [activeChats, setActiveChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  // Restored on launch the same way assigneeIdsFilter/statusesFilter/
  // unassignedFilter below already are — lazy-init from localStorage, then
  // the existing initial-load effect (which already reads current
  // chatSearch/chatSearchFilter state for its first fetchActiveChats call)
  // naturally "runs" the restored search with no extra step needed.
  const [chatSearch, setChatSearch] = useState(() => {
    if (!LS_PREFIX) return "";
    return localStorage.getItem(LS_PREFIX + "search") || "";
  });
  const [chatSearchFilter, setChatSearchFilter] = useState(() => {
    if (!LS_PREFIX) return 0;
    const v = Number(localStorage.getItem(LS_PREFIX + "searchFilter"));
    return v === 1 ? 1 : 0;
  });
  useEffect(() => {
    if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "search", chatSearch);
  }, [LS_PREFIX, chatSearch]);
  useEffect(() => {
    if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "searchFilter", String(chatSearchFilter));
  }, [LS_PREFIX, chatSearchFilter]);
  const [chatFilters, setChatFilters] = useState([]);
  const [chatFiltersLoading, setChatFiltersLoading] = useState(false);
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const [chatTotalCount, setChatTotalCount] = useState(null);
  // Server-side "filter by agent" / "filter by status" (WATI's own
  // assigneeId/status subConditions) — deliberately separate from
  // selectedFilterIndex/chatFilters (the named filter tabs): these narrow
  // whichever tab is active down further, rather than being tabs
  // themselves. Previously the sidebar's agent dropdown only filtered the
  // ~100 already-loaded chats client-side, which silently showed "no
  // chats" whenever that agent's chats hadn't happened to load yet.
  // Routing both through the same fetch as everything else fixes that at
  // the source. Both are multi-select (arrays of ids/status numbers).
  const [assigneeIdsFilter, setAssigneeIdsFilter] = useState(() => {
    if (!LS_PREFIX) return [];
    try {
      const v = JSON.parse(localStorage.getItem(LS_PREFIX + "agents"));
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  });
  const [statusesFilter, setStatusesFilter] = useState(() => {
    if (!LS_PREFIX) return [];
    try {
      const v = JSON.parse(localStorage.getItem(LS_PREFIX + "statuses"));
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  });
  // "Unassigned" — client-side only (see ChatSidebarColumn), mutually
  // exclusive with assigneeIdsFilter. Lifted up here (rather than staying
  // local to ChatSidebarColumn) so the socket handler in WatiChatContext can
  // read it when deciding whether an incoming ticket update belongs in
  // activeChats.
  const [unassignedFilter, setUnassignedFilter] = useState(() => {
    if (!LS_PREFIX) return false;
    return localStorage.getItem(LS_PREFIX + "unassigned") === "true";
  });
  useEffect(() => {
    if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "unassigned", String(unassignedFilter));
  }, [LS_PREFIX, unassignedFilter]);
  // Header's "since/between" date filter — [dayjs, dayjs] | null, always
  // scoped to last_message_time (see mapChatItem). Mutually exclusive with
  // search (enforced in ChatSidebarColumn by disabling whichever input
  // isn't active) since the two can't coexist: search puts activeChats into
  // a search-result-scoped fetch/pagination context that a date-boundary
  // scan can't meaningfully page through. Not persisted to localStorage —
  // unlike the other filters, re-running an old date range scan on every
  // launch would be an expensive surprise, not a convenience.
  const [dateRangeFilter, setDateRangeFilterRaw] = useState(null);
  const [dateScanStatus, setDateScanStatus] = useState({ scanning: false, scannedCount: 0, matchedCount: 0, paused: false, hitCap: false });
  const dateScanCancelRef = useRef(false);
  const dateScanContinueResolverRef = useRef(null);
  // A plain ref, not state — pagination only ever reads this via the ref;
  // routing it through useState+effect previously meant "Load More" clicked
  // right after the initial fetch could still see a stale value.
  const chatContinuationTokenRef = useRef(null);
  const setChatContinuationToken = (token) => { chatContinuationTokenRef.current = token; };

  // Tracks whether the currently-selected header filter tab was flipped to
  // "Assigned to me" by the agent-dropdown auto-switch below (true) versus
  // picked directly off the header dropdown by the user (false/never set).
  // Only ever revert the tab on the user's behalf when we're the ones who
  // switched it — an explicit manual pick of "Assigned to me" is left alone
  // even if the agent selection doesn't happen to be self-only.
  const autoAssignedToMeRef = useRef(false);
  // Remembers which tab was active right before the auto-switch, so moving
  // the agent selection back off "self only" restores it instead of
  // guessing a default.
  const preAutoFilterIndexRef = useRef(null);

  // Nudge staff toward search/filters instead of endlessly scrolling a huge
  // list — resets whenever a fresh (non-append) fetch starts a new search
  // context. Exempted for the "Unread" filter: someone who's already
  // filtered down to unread chats has done exactly what this nudge would
  // otherwise recommend.
  const largeListWarnedRef = useRef(false);
  const warnIfLargeList = (count) => {
    const filterName = chatFilters[selectedFilterIndex]?.name || "";
    if (/unread/i.test(filterName)) return;
    if (count > LARGE_LIST_THRESHOLD && !largeListWarnedRef.current) {
      largeListWarnedRef.current = true;
      message.info({
        key: "chat-list-large",
        content: `That's a lot of chats — you've loaded over ${LARGE_LIST_THRESHOLD}. Try searching by name/number or picking a filter instead of scrolling through all of them.`,
        duration: 8,
      });
    }
  };

  // filterList (optional) overrides the chatFilters state — needed right
  // after resolving filters for the first time, since setChatFilters(list)
  // doesn't apply synchronously: calling fetchActiveChats in the same tick
  // would otherwise still see the old (empty) chatFilters state and silently
  // fall back to filterType 0/no subConditions, i.e. unfiltered "All Chats",
  // regardless of which index was resolved as the default.
  // WATI's subConditions wire shape is a single-element array whose one
  // object carries a flat `conditions` list — multiple concurrently-active
  // conditions (a named filter's own conditions plus the assigneeId/status
  // filters) append into that same inner list, e.g.:
  // subConditions: [{ conditions: [{ type: "assigneeId", assigneeIds: [...] }, { type: "status", statuses: [...] }] }]
  const currentFilterParams = (filterIdx = selectedFilterIndex, filterList = chatFilters, assigneeIds = assigneeIdsFilter, statuses = statusesFilter) => {
    const f = filterList[filterIdx];
    const baseConditions = chatFilterSubConditions(f) || [];
    const conditions = [
      ...baseConditions,
      ...(assigneeIds?.length > 0 ? [{ type: "assigneeId", assigneeIds }] : []),
      ...(statuses?.length > 0 ? [{ type: "status", statuses }] : []),
    ];
    return { filterType: f?.filterType ?? 0, subConditions: conditions.length > 0 ? [{ conditions }] : null };
  };

  // Message-content search already has its own time-scoped WATI endpoint
  // (see the isMsgSearch branch in buildChatV2Payload just below) — when
  // that's the active search mode, the date-range filter is handed
  // straight to the API as its native from/to instead of being scanned
  // client-side, since WATI already does the real filtering there.
  const isMessageSearchScoped = (searchOptionType = chatSearchFilter, search = chatSearch) =>
    searchOptionType === 1 && !!(search || "").trim();

  const buildChatV2Payload = ({ search, searchOptionType, continuationToken = "", filterIdx, filterList, assigneeIds, statuses, dateRange = dateRangeFilter }) => {
    const trimmed = (search || "").trim();
    const { filterType, subConditions } = currentFilterParams(filterIdx, filterList, assigneeIds, statuses);
    const isMsgSearch = searchOptionType === 1 && trimmed;
    // The active date-range filter, when set, replaces the default 1-year
    // lookback — otherwise message search would ignore a range the user
    // deliberately picked in the header.
    const msgSearchWindow = dateRange
      ? { from: dateRange[0].toISOString(), to: dateRange[1].toISOString() }
      : { from: dayjs().subtract(1, "year").toISOString(), to: dayjs().toISOString() };
    return {
      userId: authState?.id,
      continuationToken,
      // Customer UI omits this entirely (backend's own default applies,
      // matching how it's always worked); supplier UI passes its channel
      // explicitly, resolved from uzd_misc mode 'wati_channel_assignments'.
      ...(channelIds ? { channelIds } : {}),
      filterType,
      subConditions,
      searchString: trimmed || null,
      searchOptionType: trimmed ? searchOptionType : null,
      ...(isMsgSearch ? msgSearchWindow : {}),
    };
  };

  const fetchActiveChats = async (search = chatSearch, filter = chatSearchFilter, filterIdx = selectedFilterIndex, filterList, assigneeIds = assigneeIdsFilter, statuses = statusesFilter, dateRange = dateRangeFilter) => {
    largeListWarnedRef.current = false;
    setChatsLoading(true);
    setHasMoreChats(true);
    try {
      const payload = buildChatV2Payload({ search, searchOptionType: filter, continuationToken: "", filterIdx, filterList, assigneeIds, statuses, dateRange });
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const mapped = raw.map(mapChatItem);
        setActiveChats(mapped);
        const token = res.data.data?.continuationToken || null;
        setChatContinuationToken(token);
        setHasMoreChats(!!token);
        setChatTotalCount(res.data.data?.count ?? null);
        warnIfLargeList(mapped.length);
        setChatsLoading(false);
        return mapped;
      }
    } catch {}
    setChatsLoading(false);
    return [];
  };

  const loadMoreChats = async () => {
    // While a date-range scan owns pagination (running or already
    // complete), ordinary scroll-triggered loading is a no-op — the scan
    // either already captured the full window, or is in the middle of
    // doing so under its own governance (see runDateRangeScan). Scrolling
    // to the bottom of a short filtered result must not silently resume
    // unbounded fetching. Message-search-scoped mode is exempt — WATI's own
    // endpoint owns the filtering there, so its normal pagination is safe.
    if (dateRangeFilter && !isMessageSearchScoped()) return;
    if (chatsLoadingMore || !hasMoreChats || chatsLoading) return;
    setChatsLoadingMore(true);
    try {
      const payload = buildChatV2Payload({ search: chatSearch, searchOptionType: chatSearchFilter, continuationToken: chatContinuationTokenRef.current || "", assigneeIds: assigneeIdsFilter, statuses: statusesFilter });
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const token = res.data.data?.continuationToken || null;
        setChatContinuationToken(token);
        let nextCount = 0;
        setActiveChats(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const merged = [...prev, ...raw.map(mapChatItem).filter(c => !existingIds.has(c.id))];
          nextCount = merged.length;
          return merged;
        });
        setHasMoreChats(!!token);
        warnIfLargeList(nextCount);
      }
    } catch {}
    setChatsLoadingMore(false);
  };

  const fetchChatsPage = async (payload) => {
    try {
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (!res.data?.success) return { list: [], token: null };
      const raw = res.data.data?.final_data || [];
      return { list: raw.map(mapChatItem), token: res.data.data?.continuationToken || null, count: res.data.data?.count ?? null };
    } catch {
      return { list: [], token: null };
    }
  };

  const countInDateRange = (list, [from, to]) =>
    list.filter(c => c.last_message_time?.isValid() && !c.last_message_time.isBefore(from) && !c.last_message_time.isAfter(to)).length;

  // Pages the list — newest chat first — until the oldest loaded chat's
  // last_message_time falls before `from`, at which point every chat in
  // [from, to] is guaranteed to already be loaded (see the design
  // discussion this came from: no server-side date filter exists for a
  // plain/Name-No. list, but its own newest-first order gives a clean stop
  // condition). Governed by DATE_SCAN_WARN_THRESHOLD (pause and ask before
  // continuing) and DATE_SCAN_HARD_CAP (hard stop, no override) so a wide
  // or deep-past range can't silently turn into an unbounded fetch loop.
  // `overrides` lets callers pass a filterIdx/assigneeIds/statuses/search/
  // searchOptionType that hasn't landed in state yet this tick (same reason
  // fetchActiveChats's own params are overridable) — e.g. switching the
  // header filter tab while a date range is active re-runs the scan
  // against the new tab; clearing the search box re-runs it without a term.
  const runDateRangeScan = async (range, overrides = {}) => {
    if (!range) return;
    const filterIdx = overrides.filterIdx ?? selectedFilterIndex;
    const assigneeIds = overrides.assigneeIds ?? assigneeIdsFilter;
    const statuses = overrides.statuses ?? statusesFilter;
    const search = overrides.search ?? chatSearch;
    const searchOptionType = overrides.searchOptionType ?? chatSearchFilter;
    const [from] = range;

    // Message-content search has its own WATI-side time window (see
    // buildChatV2Payload) — let that do the actual filtering server-side
    // instead of this scan-until-boundary loop, which only exists because
    // a plain/Name-No. list fetch has nothing server-side to lean on.
    if (isMessageSearchScoped(searchOptionType, search)) {
      dateScanCancelRef.current = false;
      setDateScanStatus({ scanning: true, scannedCount: 0, matchedCount: 0, paused: false, hitCap: false });
      const results = await fetchActiveChats(search, searchOptionType, filterIdx, undefined, assigneeIds, statuses, range);
      setDateScanStatus({ scanning: false, scannedCount: results.length, matchedCount: results.length, paused: false, hitCap: false });
      return;
    }

    dateScanCancelRef.current = false;
    largeListWarnedRef.current = false;
    setChatsLoading(true);
    setHasMoreChats(true);
    setDateScanStatus({ scanning: true, scannedCount: 0, matchedCount: 0, paused: false, hitCap: false });

    const pagePayload = (continuationToken) =>
      buildChatV2Payload({ search, searchOptionType, continuationToken, filterIdx, assigneeIds, statuses });

    const first = await fetchChatsPage(pagePayload(""));
    setActiveChats(first.list);
    setChatContinuationToken(first.token);
    setHasMoreChats(!!first.token);
    setChatTotalCount(first.count ?? null);
    setChatsLoading(false);

    let scanned = first.list.length;
    let matched = countInDateRange(first.list, range);
    let hasMore = !!first.token;
    let token = first.token;
    let oldest = first.list.length ? first.list[first.list.length - 1].last_message_time : null;
    setDateScanStatus({ scanning: true, scannedCount: scanned, matchedCount: matched, paused: false, hitCap: false });

    while (hasMore && !(oldest?.isValid() && oldest.isBefore(from))) {
      if (dateScanCancelRef.current) {
        setDateScanStatus(s => ({ ...s, scanning: false }));
        return;
      }
      if (scanned >= DATE_SCAN_HARD_CAP) {
        setDateScanStatus(s => ({ ...s, scanning: false, hitCap: true }));
        message.warning({
          key: "date-scan-cap",
          content: `Stopped after ${DATE_SCAN_HARD_CAP} chats — this range is too wide. Narrow it, or add an agent/status filter first.`,
          duration: 10,
        });
        return;
      }
      if (scanned >= DATE_SCAN_WARN_THRESHOLD) {
        setDateScanStatus(s => ({ ...s, scanning: false, paused: true }));
        const shouldContinue = await new Promise(resolve => { dateScanContinueResolverRef.current = resolve; });
        dateScanContinueResolverRef.current = null;
        if (!shouldContinue || dateScanCancelRef.current) {
          setDateScanStatus(s => ({ ...s, scanning: false, paused: false }));
          return;
        }
        setDateScanStatus(s => ({ ...s, scanning: true, paused: false }));
      }

      const page = await fetchChatsPage(pagePayload(token || ""));
      token = page.token;
      let merged;
      setActiveChats(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        merged = [...prev, ...page.list.filter(c => !existingIds.has(c.id))];
        return merged;
      });
      setChatContinuationToken(token);
      hasMore = !!token;
      setHasMoreChats(hasMore);
      scanned = merged.length;
      matched = countInDateRange(merged, range);
      oldest = merged.length ? merged[merged.length - 1].last_message_time : oldest;
      setDateScanStatus(s => ({ ...s, scannedCount: scanned, matchedCount: matched }));
    }

    setDateScanStatus(s => ({ ...s, scanning: false }));
  };

  // Setting a range kicks the scan off immediately (an explicit Apply click
  // in the popover, not live-while-picking — see ChatSidebarColumn); passing
  // null clears it and falls back to a normal fetch under whatever
  // filters/tab are currently active, same as the other filter "clear"
  // paths (resetToDefaultFilter etc.) below. `overrides` (search/
  // searchOptionType/filterIdx/...) let a caller mid-transition — e.g. the
  // sidebar's search box clearing itself back to Name/No. in the same
  // handler that re-applies the current range — pass values that haven't
  // landed in state yet this tick, same reason runDateRangeScan's own
  // overrides exist. Explicitly passing `dateRange: null` on the clear path
  // (rather than letting fetchActiveChats default to the dateRangeFilter
  // closure, which is still the pre-clear value this same tick) is what
  // makes clearing actually fall back to the plain 1-year message-search
  // window instead of silently keeping the just-cleared range.
  const setDateRangeFilter = (range, overrides = {}) => {
    setDateRangeFilterRaw(range);
    if (range) {
      runDateRangeScan(range, overrides);
    } else {
      dateScanCancelRef.current = true;
      dateScanContinueResolverRef.current?.(false);
      setDateScanStatus({ scanning: false, scannedCount: 0, matchedCount: 0, paused: false, hitCap: false });
      fetchActiveChats(
        overrides.search ?? chatSearch,
        overrides.searchOptionType ?? chatSearchFilter,
        overrides.filterIdx ?? selectedFilterIndex,
        undefined,
        overrides.assigneeIds ?? assigneeIdsFilter,
        overrides.statuses ?? statusesFilter,
        null,
      );
    }
  };

  const continueDateScan = () => { dateScanContinueResolverRef.current?.(true); };
  const cancelDateScan = () => {
    dateScanCancelRef.current = true;
    dateScanContinueResolverRef.current?.(false);
  };

  // Chat filter dropdown ("All Chats" header) — populate from WATI's saved
  // filter tabs (account-wide, not channel-scoped). When applyChatSettings
  // is true (the customer channel — QuerysetV4/Operations), narrowed
  // down to the admin's "allowed filters" list (Global Settings → Chat
  // Settings) if one is configured, default to the admin's configured
  // default filter, falling back to whichever tab WATI marks isDefault.
  // Admins are exempt from both restrictions unless the admin explicitly
  // opted themselves in via "Include admins" in that same settings screen.
  //
  // The customer channel's very first chat list render always waits for
  // this to resolve, then does its own explicit, settings-aware fetch — no
  // fast-path from a background preload. There used to be one (skip the
  // fetch and reuse an already-loaded "All Chats" preload when that
  // happened to match the resolved default), but it was a repeated source
  // of races where the page briefly or permanently showed unfiltered chats
  // regardless of what was actually configured. One extra request on load
  // is a small price for this being unconditionally correct.
  //
  // The supplier channel (WhatsappMessages, applyChatSettings: false) is a
  // separate audience with none of this: no admin restrictions, and no
  // waiting either — its chat list displays immediately from the seed
  // (an already-running background preload) or, failing that, its own
  // immediate unfiltered fetch, entirely independent of the filter-tabs
  // resolution below (which only populates its dropdown for later manual
  // switching, and never blocks or re-triggers the initial display).
  useEffect(() => {
    if (!authState?.id) return;
    if (requireChannelIds && !(channelIds?.length > 0)) return;
    setChatFiltersLoading(true);

    if (!applyChatSettings) {
      if (seedList.length > 0) {
        setActiveChats(seedList.map(mapChatItem));
        setHasMoreChats(seedList.length >= 100);
        chatContinuationTokenRef.current = seedToken;
        setChatTotalCount(seedCount);
      } else {
        fetchActiveChats(chatSearch, chatSearchFilter, 0, []);
      }
    }

    Promise.all([
      fetchChatFilters(authState.id),
      applyChatSettings ? fetchChatFilterSettings() : Promise.resolve({ allowed_filters: [], default_filter: null, include_admins: false }),
    ]).then(([fullList, settings]) => {
      const isExemptAdmin = applyChatSettings && !!authState?.user?.admin && !settings.include_admins;
      const allowed = isExemptAdmin ? [] : settings.allowed_filters;
      const defaultFilter = isExemptAdmin ? null : settings.default_filter;
      const list = allowed.length > 0 ? fullList.filter(f => allowed.includes(f.name)) : fullList;
      setChatFilters(list);
      let idx = 0;
      if (list.length > 0) {
        // A persisted user pick (localStorage, from a previous session)
        // wins over the admin's configured default — it's more specific,
        // recent intent, and still respects the allowed-filters narrowing
        // above since it's only looked up within `list`.
        const storedName = LS_PREFIX ? localStorage.getItem(LS_PREFIX + "filterName") : null;
        const storedIdx = storedName ? list.findIndex(f => f.name === storedName) : -1;
        const defaultIdx = defaultFilter ? list.findIndex(f => f.name === defaultFilter) : -1;
        idx = storedIdx >= 0 ? storedIdx : defaultIdx >= 0 ? defaultIdx : defaultChatFilterIndex(list);
        setSelectedFilterIndex(idx);
      }
      if (applyChatSettings) {
        // Pass `list` explicitly — setChatFilters(list) above hasn't
        // applied yet (state updates aren't synchronous), so without this
        // override fetchActiveChats would read the stale pre-resolution
        // chatFilters state and silently fetch unfiltered "All Chats"
        // instead.
        fetchActiveChats(chatSearch, chatSearchFilter, idx, list);
      }
    }).finally(() => setChatFiltersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.id, channelIds?.join(",")]);

  // Shared by both the user-driven onSelectFilter below and the
  // agent-dropdown auto-switch — just the state+persist+fetch, with no
  // opinion on autoAssignedToMeRef so callers can control that separately.
  const switchToFilterIndex = (idx) => {
    setSelectedFilterIndex(idx);
    if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "filterName", chatFilters[idx]?.name || "");
    if (dateRangeFilter) runDateRangeScan(dateRangeFilter, { filterIdx: idx });
    else fetchActiveChats(chatSearch, chatSearchFilter, idx);
  };

  const onSelectFilter = (idx) => {
    // A direct, explicit tab pick always wins over — and disowns — any
    // earlier auto-switch, so the agent dropdown won't later fight the
    // user's manual choice.
    autoAssignedToMeRef.current = false;
    switchToFilterIndex(idx);
  };

  // Sidebar's agent dropdown — multi-select, empty array clears back to "All
  // agents". No network call happens here (that's deferred to
  // commitAgentFilter, fired once by ChatSidebarColumn when the dropdown
  // closes) — but the header's "Assigned to me" tab sync runs right here,
  // on every click, since it's pure state (setSelectedFilterIndex) with no
  // fetch attached to it. When the selection is exactly the logged-in
  // agent, switch the header tab to "Assigned to me"; when it's anything
  // else, switch back — but only if this same auto-switch was the one that
  // put it there (see autoAssignedToMeRef), so a manual pick of that tab is
  // never clobbered. Doing this at click time (instead of waiting for the
  // dropdown to close) means the tab label updates live as you check/uncheck
  // agents, same as the badge/checkmarks already do, and it's free to do so
  // since no fetch is involved either way — by the time the dropdown closes,
  // selectedFilterIndex is already correct and commitAgentFilter (below)
  // just fetches with whatever's current.
  const onSelectAgentFilter = (assigneeIds) => {
    setAssigneeIdsFilter(assigneeIds);

    const myWatiId = authState?.user?.wati_id || null;
    const assignedToMeIdx = chatFilters.findIndex(isAssignedToMeFilterTab);
    if (!myWatiId || assignedToMeIdx < 0) return;
    const isSelfOnly = assigneeIds.length === 1 && assigneeIds[0] === myWatiId;
    if (isSelfOnly) {
      if (selectedFilterIndex === assignedToMeIdx) return;
      preAutoFilterIndexRef.current = selectedFilterIndex;
      autoAssignedToMeRef.current = true;
      setSelectedFilterIndex(assignedToMeIdx);
      if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "filterName", chatFilters[assignedToMeIdx]?.name || "");
    } else if (autoAssignedToMeRef.current) {
      autoAssignedToMeRef.current = false;
      const back = preAutoFilterIndexRef.current ?? defaultChatFilterIndex(chatFilters);
      preAutoFilterIndexRef.current = null;
      setSelectedFilterIndex(back);
      if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "filterName", chatFilters[back]?.name || "");
    }
  };

  // Fires the actual fetch for the agent dropdown — called once when the
  // dropdown closes (ChatSidebarColumn), not per click. selectedFilterIndex
  // is already resolved by onSelectAgentFilter above (including any
  // "Assigned to me" auto-switch), so this just persists and fetches with
  // whatever's current.
  const commitAgentFilter = () => {
    if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "agents", JSON.stringify(assigneeIdsFilter));
    if (dateRangeFilter) runDateRangeScan(dateRangeFilter, { assigneeIds: assigneeIdsFilter });
    else fetchActiveChats(chatSearch, chatSearchFilter, selectedFilterIndex, undefined, assigneeIdsFilter, statusesFilter);
  };

  // Sidebar's status dropdown — multi-select, empty array clears back to
  // "All statuses". Same split as the agent dropdown: state updates land
  // immediately for the live checklist, the fetch is committed once on close.
  const onSelectStatusFilter = (statuses) => setStatusesFilter(statuses);

  const commitStatusFilter = () => {
    if (LS_PREFIX) localStorage.setItem(LS_PREFIX + "statuses", JSON.stringify(statusesFilter));
    if (dateRangeFilter) runDateRangeScan(dateRangeFilter, { statuses: statusesFilter });
    else fetchActiveChats(chatSearch, chatSearchFilter, selectedFilterIndex, undefined, assigneeIdsFilter, statusesFilter);
  };

  // Reset this instance back to WATI's own default filter tab ("All Chats",
  // in practice) — used by callers that want an explicit "back to the
  // default view" action rather than preserving whatever filter is
  // currently selected. See WatiChatContext's global reconnect handling,
  // which resets the supplier channel this way but leaves the customer
  // channel on its current filter. Deliberately leaves any persisted
  // localStorage picks alone — a reconnect blip shouldn't erase a saved
  // preference; see clearSidebarFilters below for the explicit user action
  // that does.
  const resetToDefaultFilter = () => {
    autoAssignedToMeRef.current = false;
    preAutoFilterIndexRef.current = null;
    const idx = chatFilters.length > 0 ? defaultChatFilterIndex(chatFilters) : 0;
    setSelectedFilterIndex(idx);
    setAssigneeIdsFilter([]);
    setStatusesFilter([]);
    setUnassignedFilter(false);
    dateScanCancelRef.current = true;
    dateScanContinueResolverRef.current?.(false);
    setDateRangeFilterRaw(null);
    setDateScanStatus({ scanning: false, scannedCount: 0, matchedCount: 0, paused: false, hitCap: false });
    return fetchActiveChats(chatSearch, chatSearchFilter, idx, undefined, [], [], null);
  };

  // The sidebar's own "Reset filters" button — explicit user action, so
  // (unlike resetToDefaultFilter) this also clears anything persisted to
  // localStorage.
  const clearSidebarFilters = () => {
    autoAssignedToMeRef.current = false;
    preAutoFilterIndexRef.current = null;
    const idx = chatFilters.length > 0 ? defaultChatFilterIndex(chatFilters) : 0;
    setSelectedFilterIndex(idx);
    setAssigneeIdsFilter([]);
    setStatusesFilter([]);
    setUnassignedFilter(false);
    dateScanCancelRef.current = true;
    dateScanContinueResolverRef.current?.(false);
    setDateRangeFilterRaw(null);
    setDateScanStatus({ scanning: false, scannedCount: 0, matchedCount: 0, paused: false, hitCap: false });
    if (LS_PREFIX) {
      localStorage.setItem(LS_PREFIX + "filterName", chatFilters[idx]?.name || "");
      localStorage.setItem(LS_PREFIX + "agents", "[]");
      localStorage.setItem(LS_PREFIX + "statuses", "[]");
      localStorage.setItem(LS_PREFIX + "unassigned", "false");
    }
    return fetchActiveChats(chatSearch, chatSearchFilter, idx, undefined, [], [], null);
  };

  return {
    activeChats, setActiveChats,
    chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
    chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
    chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
    assigneeIdsFilter, onSelectAgentFilter, commitAgentFilter,
    statusesFilter, onSelectStatusFilter, commitStatusFilter,
    unassignedFilter, setUnassignedFilter,
    dateRangeFilter, setDateRangeFilter, dateScanStatus, continueDateScan, cancelDateScan,
    setChatContinuationToken, chatContinuationTokenRef,
    buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter, resetToDefaultFilter, clearSidebarFilters,
  };
}
